import type { RefObject } from 'react';
import { Box, Button, Collapse, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { ExpandMore as ExpandIcon, CheckCircle as PassedIcon } from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { WorkspaceFilterSection, WorkspaceFilterItem } from '../../../shared/ui/workflow/WorkspaceFilterRail';
import { getWorkflowWorkspacePalette } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { BodyText, CaptionText } from '../../../shared/ui/typography';

interface ValidationRailPassedSectionProps {
  showPassedOnly: boolean;
  onShowPassedOnly: (value: boolean) => void;
  passedCollapsed: boolean;
  onTogglePassedCollapsed: () => void;
  passedCount: number;
  onClearFilters: () => void;
  passedRef?: RefObject<HTMLDivElement | null>;
}

export default function ValidationRailPassedSection({
  showPassedOnly,
  onShowPassedOnly,
  passedCollapsed,
  onTogglePassedCollapsed,
  passedCount,
  onClearFilters,
  passedRef,
}: ValidationRailPassedSectionProps) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);

  return (
    <Box ref={passedRef} data-testid="validation-tour-target-rail-passed">
      <WorkspaceFilterSection title="Passed">
        <ActionHelpTooltip help={validationActionTooltips.passedChecksToggle} placement="right" fullWidth>
          <Button
            onClick={onTogglePassedCollapsed}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              px: 0.85,
              py: 0.75,
              border: `1px solid ${workspacePalette.sectionBorder}`,
              borderRadius: `${borderRadius.sm}px`,
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.56 : 0.18),
              color: 'text.primary',
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.28),
              },
            }}
          >
            <Stack direction="row" spacing={layoutGap.slant} alignItems="center">
              <PassedIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
              <BodyText>Passed checks</BodyText>
              <CaptionText sx={{ color: alpha(theme.palette.success.light, 0.92) }}>
                {passedCount}
              </CaptionText>
            </Stack>
            <ExpandIcon
              sx={{
                fontSize: 18,
                color: theme.palette.text.secondary,
                transform: passedCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Button>
        </ActionHelpTooltip>

        <Collapse in={!passedCollapsed}>
          <Stack spacing={layoutGap.cozy} sx={{ pl: 0.35, pr: 0.15, pt: 0.35 }}>
            <WorkspaceFilterItem
              icon={<PassedIcon sx={{ fontSize: 18 }} />}
              label="Review passed"
              count={passedCount}
              selected={showPassedOnly}
              onClick={() => onShowPassedOnly(true)}
              tone="success"
              help={validationActionTooltips.reviewPassedChecks}
            />
            <BodyText
              sx={{
                color: theme.palette.text.secondary,
                px: 0.8,
              }}
            >
              Keep passed checks available without cluttering the unresolved issue queue.
            </BodyText>
            <ActionHelpTooltip help={validationActionTooltips.resetFilters} placement="right" fullWidth>
              <Button
                size="small"
                onClick={onClearFilters}
                sx={{ justifyContent: 'flex-start', px: 0.8, width: '100%' }}
              >
                Reset filters
              </Button>
            </ActionHelpTooltip>
          </Stack>
        </Collapse>
      </WorkspaceFilterSection>
    </Box>
  );
}
